import {useCallback, useEffect, useRef, useState} from "react";
import {Button, Card, Empty, Form, Spin, Statistic, Tooltip} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import {Bar} from "react-chartjs-2";
import moment from "moment";
import {t} from "i18next";
import DatePicker from "../../../core/components/DatePicker";
import QuickDatePicker from "../../../core/components/data-grid/QuickDatePicker";
import {DATE_OPTIONS, getBeginStr, getDateFromNow, getEndStr, getUserTimeZone} from "../../../core/utils/date-utils";
import {barDataset, barOptions, useChartTheme} from "./chart-theme";
import HospitalSelectBox from "../../hospital/HospitalSelectBox";

/**
 * Pano uç noktalarının filtresi: yerel gün sınırları UTC olarak + günlük kırılım için saat dilimi.
 * hospitalIdList yalnız Genel bakış gönderir; boşsa tüm hastaneler.
 */
export const buildPanelFilter = (begin, end, hospitalIdList) => ({
    begin: getBeginStr(begin),
    end: getEndStr(end),
    timeZone: getUserTimeZone(),
    hospitalIdList: hospitalIdList?.length ? hospitalIdList : null,
})

export const defaultPanelFilter = () => {
    const date = getDateFromNow(DATE_OPTIONS.THIS_MONTH)
    return buildPanelFilter(date.start, date.end)
}

/** Tarih aralığı çubuğu — İstatistikler sekmesindeki süzgeçle aynı düzen. withHospital: hastane çoklu seçimi. */
export function PanelRangeBar({onSearch, extra, withHospital}) {
    const [form] = Form.useForm()
    const initial = useRef(getDateFromNow(DATE_OPTIONS.THIS_MONTH)).current

    const submit = () => {
        const {begin, end, hospitalIdList} = form.getFieldsValue()
        if (begin && end)
            onSearch(buildPanelFilter(begin, end, hospitalIdList))
    }

    return (
        <Card size={'small'} style={{marginBottom: 12}}>
            <Form form={form} size={'small'} initialValues={{begin: initial.start, end: initial.end}} onFinish={submit}>
                <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                    <QuickDatePicker onChange={(start, end) => {
                        form.setFieldsValue({begin: start, end: end})
                        submit()
                    }}/>
                    <Form.Item style={{marginBottom: 0}} name="begin">
                        <DatePicker showToday={false} allowClear={false}/>
                    </Form.Item>
                    <Form.Item style={{marginBottom: 0}} name="end">
                        <DatePicker showToday={false} allowClear={false}/>
                    </Form.Item>
                    {withHospital && (
                        <Form.Item style={{marginBottom: 0, minWidth: 240}} name="hospitalIdList">
                            <HospitalSelectBox maxTagCount={'responsive'} placeholder={t('All hospitals')}/>
                        </Form.Item>
                    )}
                    <Button type={'primary'} htmlType={'submit'}>{t('Search')}</Button>
                    {extra}
                </div>
            </Form>
        </Card>
    )
}

/** filter değiştikçe fetcher'ı çağırır; yarışta geç dönen eski yanıt yenisini ezmez. */
export function usePanelData(fetcher, filter) {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const seq = useRef(0)

    const load = useCallback(async () => {
        if (!filter)
            return
        const id = ++seq.current
        setLoading(true)
        let res = null
        try {
            res = await fetcher(filter)
        } finally {
            if (id === seq.current) {
                setData(res?.success ? res.data : null)
                setLoading(false)
            }
        }
    }, [fetcher, filter])

    useEffect(() => {
        load()
    }, [load])

    return {data, loading}
}

export function PanelSpin({loading, children}) {
    return <Spin spinning={loading}>{children}</Spin>
}

/** Başlık + (i): üzerine gelince/dokununca kartın hangi sayımı gösterdiğini açıklar. */
export function InfoTitle({title, info}) {
    if (!info)
        return title

    return (
        <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
            {title}
            <Tooltip title={info} trigger={['hover', 'click']}>
                <InfoCircleOutlined style={{color: 'var(--text-3)', cursor: 'help', fontSize: 13}}/>
            </Tooltip>
        </span>
    )
}

/** Tek sayı kartı. hint: sayının altındaki açıklama (yüzde, tarih vb.); info: (i) açıklaması; suffix: birim. */
export function Tile({title, value, hint, info, suffix}) {
    return (
        <Card size={'small'} style={{height: '100%'}}>
            <Statistic title={<InfoTitle title={title} info={info}/>} value={value ?? 0} suffix={suffix}/>
            {hint && <div style={{color: 'var(--text-3)', fontSize: 12, marginTop: 2}}>{hint}</div>}
        </Card>
    )
}

export function ChartCard({title, info, height = 240, empty, children}) {
    return (
        <Card size={'small'} title={<InfoTitle title={title} info={info}/>} style={{height: '100%'}}>
            {empty
                ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No data')}/>
                : <div style={{position: 'relative', height}}>{children}</div>}
        </Card>
    )
}

/** Günlük sayı grafiği. daily: [{day: 'yyyy-MM-dd', count}] — sunucu boş günleri 0 ile doldurur. */
export function DailyBarChart({daily, title, info, height}) {
    const theme = useChartTheme()
    const items = daily || []
    const labels = items.map(x => moment(x.day).format('DD.MM'))

    const options = barOptions(theme, {
        tooltipTitle: (ctx) => moment(items[ctx[0].dataIndex]?.day).format('DD.MM.YYYY dddd'),
    })

    return (
        <ChartCard title={title} info={info} height={height} empty={!items.some(x => x.count > 0)}>
            <Bar
                key={theme.mode}
                data={{labels, datasets: [barDataset(theme, items.map(x => x.count))]}}
                options={options}
            />
        </ChartCard>
    )
}

/**
 * Kırılım grafiği (yatay çubuk). items: [{id, name, count}], büyükten küçüğe.
 * slotOf: kategori → seri renk sırası (null = nötr gri). Renk sıraya değil kategoriye bağlı olsun
 * diye sabit eşlemeden gelir; tarih aralığı değişince aynı kategori aynı renkte kalır.
 */
export function CountBarChart({items, title, info, labelOf, slotOf, height}) {
    const theme = useChartTheme()
    const list = (items || []).filter(x => x.count > 0)
    const labels = list.map(x => labelOf ? labelOf(x) : (x.name ?? '-'))
    const chartHeight = height ?? Math.max(120, list.length * 30 + 30)

    return (
        <ChartCard title={title} info={info} height={chartHeight} empty={!list.length}>
            <Bar
                key={theme.mode}
                data={{labels, datasets: [barDataset(theme, list.map(x => x.count), slotOf ? list.map(slotOf) : 0)]}}
                options={barOptions(theme, {horizontal: true})}
            />
        </ChartCard>
    )
}

const STUDY_TYPE_KEYS = {1: 'ROUTINE', 2: 'INPATIENT', 3: 'EMERGENCY', 4: 'COMPARATIVE_EXAMINATION'}

export const studyTypeLabel = (id) => STUDY_TYPE_KEYS[id] ? t(STUDY_TYPE_KEYS[id]) : t('Not set')

// Çekim tipi → seri rengi. 4 tip, 4 renk; Acil turuncu (--chart-2). Tipsiz olan nötr gri.
const STUDY_TYPE_SLOTS = {1: 0, 3: 1, 2: 2, 4: 3}

export const studyTypeSlot = (x) => STUDY_TYPE_SLOTS[x.id] ?? null

// Modalite → seri rengi. Palet 4 renk; dışarıda kalan modaliteler nötr griye düşer (yeni ton üretilmez).
const MODALITY_SLOTS = {CT: 0, MR: 1, DX: 2, US: 3}

export const modalitySlot = (x) => MODALITY_SLOTS[(x.name || '').toUpperCase()] ?? null

/** Günlük ortalama ve en yoğun gün — sayı kartlarının ipuçları. */
export const dailySummary = (daily) => {
    const items = daily || []
    const total = items.reduce((a, x) => a + x.count, 0)
    const busiest = items.reduce((best, x) => (!best || x.count > best.count ? x : best), null)
    return {
        average: items.length ? Math.round((total / items.length) * 10) / 10 : 0,
        busiest: busiest && busiest.count > 0 ? busiest : null,
    }
}

export const percent = (part, whole) => whole ? `%${Math.round((part * 1000) / whole) / 10}` : '%0'
